import { AiPromptsManager } from "@/components/ai/ai-prompts-manager";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/components/ui/use-toast";
import { useProject } from '@/hooks/useProjects';
import { BulkChapterExport } from "@/pages/chapters/components/bulk-chapter-export";
import { storyContextService } from '@/services/story-context-service';
import { PromptScope } from "@/services/supabase/ai-prompts";
import { supabase } from "@/services/supabase/supabase";
import { Story } from "@/types/workspace";
import { Book, FileDown, RefreshCw, Settings2, Sparkles, Wand2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useLicense } from '@/hooks/useLicense';

export function SectionProject() {
    const { projectId } = useParams();
    const { data: project } = useProject(projectId!);
    const { hasAIFeatures } = useLicense();

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium">Project Management</h3>
                <p className="text-sm text-muted-foreground">
                    Control stories, export data, and manage AI context.
                </p>
            </div>
            <Separator />

            <div className="grid gap-6">
                {/* Manage Stories */}
                <ManageStoriesCard />

                {/* Story Context Management - AI only */}
                {hasAIFeatures && <StoryContextManagementCard />}

                {/* AI Prompts Management - AI only */}
                {hasAIFeatures && project && (
                    <Card className="border-muted bg-card/50">
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                    <Wand2 className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Project Prompts</CardTitle>
                                    <CardDescription>
                                        Define custom AI instructions and writing actions for this project.
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <AiPromptsManager
                                scope={PromptScope.Project}
                                contextId={project.id}
                                title="Project Instructions"
                                description="These prompts will be active for all stories and chapters in this project."
                            />
                        </CardContent>
                    </Card>
                )}

                {/* Export All Chapters */}
                {project && (
                    <Card className="border-muted bg-card/50">
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <div className="p-2 bg-primary/10 rounded-lg">
                                    <FileDown className="h-5 w-5 text-primary" />
                                </div>
                                <div>
                                    <CardTitle className="text-base">Export Content</CardTitle>
                                    <CardDescription>
                                        Download your entire project data
                                    </CardDescription>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-4">
                                <div className="bg-muted/30 p-4 rounded-lg text-sm border border-dashed">
                                    <p className="text-muted-foreground mb-4">
                                        Download all chapters from this project as individual files in a ZIP archive.
                                        Each chapter will be exported using the same high-quality formatting as the individual chapter export feature.
                                    </p>
                                    <BulkChapterExport
                                        projectId={project.id.toString()}
                                        projectName={project.name}
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
            </div>
        </div>
    );
}

function ManageStoriesCard() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [stories, setStories] = useState<Story[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [storyBeingDeleted, setStoryBeingDeleted] = useState<string | null>(null);
    const [selectedStoryForAI, setSelectedStoryForAI] = useState<Story | null>(null);
    const { hasAIFeatures } = useLicense();

    useEffect(() => {
        const fetchStories = async () => {
            if (!projectId) return;

            try {
                setIsLoading(true);
                // Simulate network delay for skeleton demo
                await new Promise(resolve => setTimeout(resolve, 800));

                const { data, error } = await supabase
                    .from('stories')
                    .select('*')
                    .eq('project_id', projectId);

                if (error) throw error;

                setStories(data || []);
            } catch (error) {
                console.error('Error fetching stories:', error);
                toast({
                    title: "Error",
                    description: "Failed to load stories",
                    variant: "destructive"
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchStories();
    }, [projectId, toast]);

    const handleDeleteStory = async (storyId: string) => {
        try {
            setStoryBeingDeleted(storyId);

            const { error } = await supabase
                .from('stories')
                .delete()
                .eq('id', storyId);

            if (error) throw error;

            setStories(stories.filter(story => story.id !== storyId));

            toast({
                title: "Success",
                description: "Story deleted successfully",
                variant: "default"
            });
        } catch (error) {
            console.error('Error deleting story:', error);
            toast({
                title: "Error",
                description: "Failed to delete story. Please try again.",
                variant: "destructive"
            });
        } finally {
            setStoryBeingDeleted(null);
        }
    };

    return (
        <>
            <Card className="border-muted bg-card/50">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <div className="p-2 bg-primary/10 rounded-lg">
                            <Book className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                            <CardTitle className="text-base">Stories</CardTitle>
                            <CardDescription>
                                Manage the stories within your project
                            </CardDescription>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="space-y-3">
                            {[1, 2].map((i) => (
                                <div key={i} className="flex items-center justify-between p-4 border rounded-lg bg-card">
                                    <div className="space-y-2 flex-1">
                                        <div className="h-4 w-1/3 bg-muted animate-pulse rounded" />
                                        <div className="h-3 w-2/3 bg-muted/50 animate-pulse rounded" />
                                    </div>
                                    <div className="flex space-x-2 ml-4">
                                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                                        <div className="h-8 w-16 bg-muted animate-pulse rounded" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : stories.length === 0 ? (
                        <div className="py-8 text-center text-muted-foreground border border-dashed rounded-lg bg-muted/20">
                            <p>No stories found in this project.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {stories.map((story) => (
                                <div key={story.id} className="group grid grid-cols-[1fr,auto] items-center gap-4 p-4 border rounded-lg hover:border-primary/50 transition-colors bg-card">
                                    <div className="min-w-0 space-y-1">
                                        <h3 className="font-medium truncate group-hover:text-primary transition-colors">{story.name}</h3>
                                        <p className="text-sm text-muted-foreground truncate">
                                            {story.plotline || 'No description provided'}
                                        </p>
                                    </div>
                                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        {hasAIFeatures && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setSelectedStoryForAI(story)}
                                            title="AI Configuration"
                                        >
                                            <Settings2 className="h-4 w-4 mr-2" />
                                            AI
                                        </Button>
                                        )}
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => navigate(`/projects/${projectId}/stories/${story.id}/overview`)}
                                        >
                                            View
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                            onClick={() => handleDeleteStory(story.id)}
                                            disabled={storyBeingDeleted === story.id || stories.length <= 1}
                                            title={stories.length <= 1 ? "Cannot delete the last remaining story" : ""}
                                        >
                                            {storyBeingDeleted === story.id ? 'Deleting...' : 'Delete'}
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <Dialog open={!!selectedStoryForAI} onOpenChange={(open) => !open && setSelectedStoryForAI(null)}>
                <DialogContent className="sm:max-w-[700px] max-h-[85vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Story AI Configuration</DialogTitle>
                        <DialogDescription>
                            Manage AI prompts for <span className="font-semibold">{selectedStoryForAI?.name}</span>.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedStoryForAI && (
                        <div className="flex-1 overflow-y-auto pr-1">
                            <AiPromptsManager
                                scope="story"
                                contextId={parseInt(selectedStoryForAI.id)}
                                title="Story Instructions"
                                description="These prompts will be active for all chapters in this story."
                            />
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
}

function StoryContextManagementCard() {
    const { projectId } = useParams();
    const { toast } = useToast();
    const [stories, setStories] = useState<Story[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [reindexingStories, setReindexingStories] = useState<Set<string>>(new Set());

    useEffect(() => {
        const loadStories = async () => {
            if (!projectId) return;
            setIsLoading(true);
            try {
                const { data, error } = await supabase
                    .from('stories')
                    .select('*')
                    .eq('project_id', projectId)
                    .order('created_at', { ascending: true });
                if (error) throw error;
                setStories(data || []);
            } catch (error) {
                toast({ title: "Error", description: "Failed to load stories", variant: "destructive" });
            } finally {
                setIsLoading(false);
            }
        };
        loadStories();
    }, [projectId, toast]);

    const handleReindexStory = async (storyId: string, storyName: string) => {
        if (!projectId) return;
        setReindexingStories(prev => new Set(prev).add(storyId));
        try {
            const result = await storyContextService.initializeContext(storyId, projectId, true);
            if (result.success) {
                toast({ title: "Success", description: `Story context for "${storyName}" has been re-indexed.` });
            } else {
                throw new Error(result.error || 'Unknown error');
            }
        } catch (error) {
            toast({ title: "Error", description: `Failed to re-index: ${error instanceof Error ? error.message : 'Unknown error'}`, variant: "destructive" });
        } finally {
            setReindexingStories(prev => {
                const newSet = new Set(prev);
                newSet.delete(storyId);
                return newSet;
            });
        }
    };

    const handleReindexAll = async () => {
        if (!projectId || stories.length === 0) return;
        setReindexingStories(new Set(stories.map(s => s.id)));
        let successCount = 0;

        for (const story of stories) {
            try {
                const result = await storyContextService.initializeContext(story.id, projectId, true);
                if (result.success) successCount++;
            } catch (error) {
                console.error(error);
            }
        }
        setReindexingStories(new Set());
        toast({ title: "Success", description: `Re-indexed ${successCount} stories.` });
    };


    return (
        <Card className="border-muted bg-card/50">
            <CardHeader>
                <div className="flex items-center gap-2">
                    <div className="p-2 bg-primary/10 rounded-lg">
                        <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <CardTitle className="text-base">AI Context</CardTitle>
                        <CardDescription>
                            Refresh the AI's understanding of your stories
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                {isLoading ? (
                    <div className="py-4 text-center text-muted-foreground">Loading...</div>
                ) : (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between p-4 border border-dashed rounded-lg bg-muted/20">
                            <div>
                                <h4 className="font-medium">Reset All Context</h4>
                                <p className="text-sm text-muted-foreground">
                                    Force re-index all stories to update AI memory
                                </p>
                            </div>
                            <Button
                                onClick={handleReindexAll}
                                disabled={reindexingStories.size > 0}
                                variant="secondary"
                                size="sm"
                            >
                                {reindexingStories.size > 0 ? (
                                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                                ) : (
                                    <RefreshCw className="h-4 w-4 mr-2" />
                                )}
                                Re-index All
                            </Button>
                        </div>

                        <div className="space-y-1">
                            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">Individual Stories</h4>
                            {stories.map((story) => (
                                <div key={story.id} className="flex items-center justify-between p-2 hover:bg-muted/50 rounded-md transition-colors">
                                    <div className="flex items-center gap-3">
                                        <Badge variant="outline" className="font-normal">{story.status || 'Draft'}</Badge>
                                        <span className="font-medium text-sm">{story.name}</span>
                                    </div>
                                    <Button
                                        onClick={() => handleReindexStory(story.id, story.name)}
                                        disabled={reindexingStories.has(story.id)}
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                    >
                                        <RefreshCw className={`h-3 w-3 ${reindexingStories.has(story.id) ? 'animate-spin' : ''}`} />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
